import { useEffect, useRef, useState } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import axiosConfig from '@/data/utils/axiosConfigInstance';
import Toast from '@/utils/toastManager';
import { trackManageSubscriptionEvent } from '@/data/utils/manageSubscriptionAnalytics';
import { SocialMediaLinks } from '@/constants/SocialMediaLinks';
import { isStudioUser } from '@/utils/isStudioUser';
import './CancellationRetentionModal.css';

const STEPS = { CONFIRM: 0, REASON: 1, REASON_OFFER: 2, LIKELIHOOD: 3, FEEDBACK: 4, LAST_CHANCE: 5 };

const REASONS = [
	{ value: 'too_expensive', label: 'Too Expensive' },
	{ value: 'not_using', label: 'Not Using it Enough' },
	{ value: 'taking_break', label: 'Taking a Break' },
	{ value: 'too_complicated', label: 'Too Complicated' },
	{ value: 'low_value', label: 'Low Value' },
	{ value: 'technical', label: 'Technical Difficulties' },
	{ value: 'other', label: 'Other' }
];

const resolveRetentionOffer = (
	selectedReason,
	{
		isTrial,
		isAnnual,
		isStudio = false,
		isPauseScheduled = false,
		pauseMonths = 1,
		trialExtended = false,
		bonusMonthAdded = false,
		retentionCouponApplied = false
	} = {}
) => {
	if (!selectedReason || isStudio) {
		return { offerType: 'none', isEligible: false, noOfferReason: isStudio ? 'studio' : '' };
	}

	if (selectedReason === 'technical') {
		if (isTrial) {
			if (trialExtended) {
				return { offerType: 'trial_extension_7d', isEligible: true, noOfferReason: 'already_claimed' };
			}
			return { offerType: 'trial_extension_7d', isEligible: true, noOfferReason: '' };
		}
		if (isPauseScheduled) {
			return { offerType: 'pause_1_months', isEligible: true, noOfferReason: 'pause_already_scheduled' };
		}
		return { offerType: 'pause_1_months', isEligible: true, noOfferReason: '' };
	}

	if (isTrial) {
		if (trialExtended) {
			return { offerType: 'none', isEligible: false, noOfferReason: 'already_claimed' };
		}
		if (selectedReason === 'not_using' || selectedReason === 'too_complicated') {
			return { offerType: 'trial_extension_7d', isEligible: true, noOfferReason: '' };
		}
		return { offerType: 'none', isEligible: false, noOfferReason: 'trial_non_eligible_reason' };
	}

	if (selectedReason === 'taking_break' || selectedReason === 'other') {
		if (isPauseScheduled) {
			return { offerType: 'none', isEligible: false, noOfferReason: 'pause_already_scheduled' };
		}
		const safeMonths = [1, 2, 3].includes(pauseMonths) ? pauseMonths : 1;
		return { offerType: `pause_${safeMonths}_months`, isEligible: true, noOfferReason: '' };
	}

	if (selectedReason === 'not_using') {
		if (isAnnual) {
			if (bonusMonthAdded) {
				return { offerType: 'none', isEligible: false, noOfferReason: 'already_claimed' };
			}
			return { offerType: 'bonus_month_annual', isEligible: true, noOfferReason: '' };
		}
		if (retentionCouponApplied) {
			return { offerType: 'none', isEligible: false, noOfferReason: 'already_claimed' };
		}
		return { offerType: 'free_month_monthly', isEligible: true, noOfferReason: '' };
	}

	if (selectedReason === 'too_expensive' || selectedReason === 'low_value') {
		if (retentionCouponApplied) {
			return { offerType: 'none', isEligible: false, noOfferReason: 'already_claimed' };
		}
		return {
			offerType: isAnnual ? 'discount_25_annual' : 'discount_50_monthly',
			isEligible: true,
			noOfferReason: ''
		};
	}

	if (selectedReason === 'too_complicated') {
		if (isAnnual) {
			if (bonusMonthAdded) {
				return { offerType: 'none', isEligible: false, noOfferReason: 'already_claimed' };
			}
			return { offerType: 'bonus_month_annual', isEligible: true, noOfferReason: '' };
		}
		if (retentionCouponApplied) {
			return { offerType: 'none', isEligible: false, noOfferReason: 'already_claimed' };
		}
		return { offerType: 'free_month_monthly', isEligible: true, noOfferReason: '' };
	}

	return { offerType: 'none', isEligible: false, noOfferReason: '' };
};

const getCancellationDateTimestamp = details =>
	details?.cancelledAt || details?.currentTermEnd || details?.current_term_end || details?.nextBillingAt || null;

const formatLongDate = unixTimestamp => {
	if (!unixTimestamp) return null;

	const date = new Date(unixTimestamp * 1000);
	if (Number.isNaN(date.getTime())) return null;

	return date.toLocaleDateString('en-US', {
		timeZone: 'America/New_York',
		month: 'long',
		day: 'numeric',
		year: 'numeric'
	});
};

const getAppleOfferDetails = (selectedReason, { isAnnual, isTrial } = {}) => {
	if (isTrial) {
		if (selectedReason === 'not_using' || selectedReason === 'too_complicated') {
			return {
				title: 'Exclusive Trial Extension on Web Checkout',
				deal: 'an extra 7 days of full access completely free of charge'
			};
		}
		return {
			title: 'Exclusive Offer for Web Checkout',
			deal: 'exclusive web checkout pricing and promotional plans'
		};
	}

	if (selectedReason === 'too_expensive' || selectedReason === 'low_value') {
		return {
			title: isAnnual ? 'Save 25% by Switching to Web Checkout' : 'Save 50% for 3 Months on Web Checkout',
			deal: isAnnual ? '25% off your annual renewal invoice' : '50% off your next 3 monthly invoices'
		};
	}

	if (selectedReason === 'not_using') {
		return {
			title: isAnnual ? 'Get 1 Bonus Month Free on Web Checkout' : 'Get 1 Free Month on Web Checkout',
			deal: isAnnual
				? '1 full bonus month added to your annual membership at zero additional charge'
				: '1 full month 100% free on your next invoice'
		};
	}

	if (selectedReason === 'taking_break' || selectedReason === 'other') {
		return {
			title: 'Flexible Membership Pauses on Web Checkout',
			deal: 'the ability to pause billing for 1, 2, or 3 months while keeping all your saved videos and playlists completely intact'
		};
	}

	if (selectedReason === 'too_complicated') {
		return {
			title: isAnnual ? 'Quick-Start Guide + 1 Bonus Month on Web' : 'Quick-Start Guide + 1 Free Month on Web',
			deal: isAnnual
				? 'our step-by-step Quick-Start Guide plus 1 bonus month added free to your annual plan'
				: 'our step-by-step Quick-Start Guide plus 1 full month 100% free'
		};
	}

	if (selectedReason === 'technical') {
		return {
			title: '1-Month Courtesy Pause on Web Checkout',
			deal: 'a 1-month billing pause while our engineering and support team works with you to resolve any technical issues'
		};
	}

	return {
		title: 'Exclusive Offer for Web Checkout',
		deal: 'special web-only retention discounts and benefits'
	};
};

function CancellationRetentionModal({
	open,
	onClose,
	onConfirmCancel,
	onRequestPause,
	onSubscriptionUpdated,
	subscriptionDetails
}) {
	const isPaused = subscriptionDetails?.status?.toLowerCase() === 'paused';
	const isTrial =
		subscriptionDetails?.status?.toLowerCase() === 'in_trial' ||
		Boolean(subscriptionDetails?.trialEnd && Number(subscriptionDetails.trialEnd) * 1000 > Date.now());
	const isTrialExtended = Boolean(subscriptionDetails?.trialExtended || subscriptionDetails?.meta?.trialExtended);
	const isApple =
		subscriptionDetails?.channel === 'app_store' || subscriptionDetails?.paymentSource === 'apple_pay_in_app';

	const [bonusMonthAddedLocal, setBonusMonthAddedLocal] = useState(false);
	const [retentionCouponAppliedLocal, setRetentionCouponAppliedLocal] = useState(false);
	const [pauseScheduledLocal, setPauseScheduledLocal] = useState(false);

	const isPauseScheduled = Boolean(
		pauseScheduledLocal ||
			subscriptionDetails?.hasScheduledPause ||
			subscriptionDetails?.pauseDate ||
			subscriptionDetails?.pause_date ||
			subscriptionDetails?.resumeDate ||
			subscriptionDetails?.resume_date ||
			isPaused
	);

	const isBonusMonthAdded = Boolean(
		bonusMonthAddedLocal ||
			subscriptionDetails?.bonusMonthAdded ||
			subscriptionDetails?.meta?.bonusMonthAdded ||
			subscriptionDetails?.meta?.bonusMonthAddedForTerm
	);
	const isRetentionCouponApplied = Boolean(
		retentionCouponAppliedLocal ||
			subscriptionDetails?.retentionCouponApplied ||
			subscriptionDetails?.meta?.retentionCouponApplied ||
			(subscriptionDetails?.coupons &&
				subscriptionDetails.coupons.some(c =>
					[
						'REJOIN-25',
						'REJOIN-20',
						'REJOIN-50',
						'FREEMONTH',
						'FREE-MONTH',
						'REJOIN25',
						'REJOIN20',
						'REJOIN50'
					].includes(String(c).toUpperCase())
				))
	);

	const [step, setStep] = useState(STEPS.CONFIRM);
	const [reason, setReason] = useState('');
	const [otherText, setOtherText] = useState('');
	const [likelihood, setLikelihood] = useState(null);
	const [feedback, setFeedback] = useState('');
	const [acknowledged, setAcknowledged] = useState(false);
	const [couponApplied, setCouponApplied] = useState(false);
	const [couponOffered, setCouponOffered] = useState(false);
	const [offerOffered, setOfferOffered] = useState(false);
	const [offerAccepted, setOfferAccepted] = useState(false);
	const [offerType, setOfferType] = useState('');
	const [selectedPauseMonths, setSelectedPauseMonths] = useState(1);
	const [submitting, setSubmitting] = useState(false);
	const [cancelOption] = useState('end_of_term');
	const startedAtRef = useRef(null);
	const visitedStepsRef = useRef(new Set([STEPS.CONFIRM]));
	// One tracking session per opening of the flow. Saves are chained on `pending` so every
	// request carries the record id created by the first one, and responses that land after
	// the modal reopens can't leak into the next attempt.
	const createTrackingSession = () => ({ retentionId: null, pending: Promise.resolve(), completed: false });
	const trackingSessionRef = useRef(createTrackingSession());
	const offerAcceptedRef = useRef(false);
	const offerTypeRef = useRef('');

	const planId = subscriptionDetails?.planId || '';
	const billingPeriodUnit = subscriptionDetails?.billingFrequency || subscriptionDetails?.billingPeriodUnit || '';
	const billingPeriod = subscriptionDetails?.billingPeriod || null;
	const isPlus = planId.toLowerCase().includes('vip');
	const isStudio = Boolean(
		planId.toLowerCase().includes('studio') ||
			String(subscriptionDetails?.planName || '')
				.toLowerCase()
				.includes('studio') ||
			String(subscriptionDetails?.subscription_name || '')
				.toLowerCase()
				.includes('studio') ||
			isStudioUser(subscriptionDetails)
	);

	const periodUnit = `${billingPeriodUnit || ''}`.toLowerCase();
	const period = Number(billingPeriod);
	const normalizedPlanId = `${planId || ''}`.toLowerCase();
	const isAnnual =
		['year', 'yearly', 'annual', 'annually'].some(term => periodUnit.includes(term)) ||
		(Number.isFinite(period) && period >= 12 && periodUnit.includes('month')) ||
		['year', 'annual'].some(term => normalizedPlanId.includes(term));

	const cancellationDateLabel = formatLongDate(getCancellationDateTimestamp(subscriptionDetails));

	useEffect(() => {
		if (!open) return;
		startedAtRef.current = new Date();
		visitedStepsRef.current = new Set([STEPS.CONFIRM]);
		trackingSessionRef.current = createTrackingSession();
		offerAcceptedRef.current = false;
		offerTypeRef.current = '';

		saveRetentionProgress(STEPS.CONFIRM, {
			startNew: true,
			statusAfter: 'in_progress',
			outcome: 'in_progress',
			pauseMonths: null,
			planId: planId || subscriptionDetails?.planId || '',
			billingCadence: isAnnual ? 'annual' : 'monthly',
			platform: isApple ? 'apple' : 'web',
			isTrialUser: isTrial,
			isAppleUser: isApple
		});
	}, [open]);

	const getOfferOptions = (extra = {}) => ({
		isTrial,
		isAnnual,
		isStudio,
		pauseMonths: selectedPauseMonths,
		trialExtended: isTrialExtended,
		bonusMonthAdded: isBonusMonthAdded,
		retentionCouponApplied: isRetentionCouponApplied,
		isPauseScheduled,
		...extra
	});

	useEffect(() => {
		visitedStepsRef.current.add(step);
		const { offerType: resolvedType, isEligible, noOfferReason } = resolveRetentionOffer(reason, getOfferOptions());
		setOfferType(resolvedType);
		offerTypeRef.current = resolvedType;
		const isCouponType = [
			'free_month_monthly',
			'bonus_month_annual',
			'discount_50_monthly',
			'discount_25_annual'
		].includes(resolvedType);
		if (step === STEPS.REASON_OFFER) {
			setOfferOffered(isEligible && !noOfferReason);
			setCouponOffered(isEligible && isCouponType && !noOfferReason);
		}
	}, [
		step,
		reason,
		selectedPauseMonths,
		isTrial,
		isAnnual,
		isStudio,
		isTrialExtended,
		isBonusMonthAdded,
		isRetentionCouponApplied,
		isPauseScheduled
	]);

	const getRetentionId = async () => {
		const session = trackingSessionRef.current;
		await session.pending;
		return session.retentionId;
	};

	const markTrackingCompleted = () => {
		trackingSessionRef.current.completed = true;
	};

	const saveRetentionProgress = (currentStep, overrides = {}) => {
		const session = trackingSessionRef.current;
		let payload;
		try {
			const activeReason = overrides.reason !== undefined ? overrides.reason : reason;
			const {
				offerType: currentOfferType,
				isEligible,
				noOfferReason: currentNoOfferReason
			} = resolveRetentionOffer(
				activeReason,
				getOfferOptions({
					pauseMonths: overrides.pauseMonths !== undefined ? overrides.pauseMonths : selectedPauseMonths
				})
			);

			const effectiveType = overrides.offerType || offerTypeRef.current || currentOfferType;
			const isPauseOffer =
				!isTrial &&
				(Boolean(overrides.pauseMonths) || (typeof effectiveType === 'string' && effectiveType.startsWith('pause_')));

			const resolvedPauseMonths =
				overrides.pauseMonths !== undefined ? overrides.pauseMonths : isPauseOffer ? selectedPauseMonths : null;

			const isCouponType = [
				'free_month_monthly',
				'bonus_month_annual',
				'discount_50_monthly',
				'discount_25_annual'
			].includes(effectiveType);

			const resolvedNoOfferReason =
				overrides.noOfferReason !== undefined
					? overrides.noOfferReason
					: isApple
						? 'apple'
						: currentNoOfferReason || '';

			const resolvedOfferOffered =
				overrides.offerOffered !== undefined ? overrides.offerOffered : isEligible && !resolvedNoOfferReason;

			payload = {
				step: currentStep !== undefined ? currentStep : step,
				planId: overrides.planId || planId || subscriptionDetails?.planId || '',
				billingCadence: overrides.billingCadence || (isAnnual ? 'annual' : 'monthly'),
				platform: isApple ? 'apple' : 'web',
				pauseMonths: resolvedPauseMonths,
				reason: activeReason,
				otherText: overrides.otherText !== undefined ? overrides.otherText : otherText,
				likelihood: overrides.likelihood !== undefined ? overrides.likelihood : likelihood,
				feedback: overrides.feedback !== undefined ? overrides.feedback : feedback,
				offerType: effectiveType,
				offerOffered: resolvedOfferOffered,
				offerAccepted:
					overrides.offerAccepted !== undefined ? overrides.offerAccepted : offerAcceptedRef.current || offerAccepted,
				couponOffered:
					overrides.couponOffered !== undefined
						? overrides.couponOffered
						: isEligible && isCouponType && !resolvedNoOfferReason,
				couponApplied: overrides.couponApplied !== undefined ? overrides.couponApplied : couponApplied,
				noOfferReason: resolvedNoOfferReason,
				isTrialUser: isTrial,
				isAppleUser: isApple,
				visitedSteps: Array.from(visitedStepsRef.current),
				clientStartedAt: startedAtRef.current?.toISOString?.() ?? null,
				source: 'cancellation_retention_modal',
				...overrides
			};
		} catch {
			return session.pending;
		}

		session.pending = session.pending.then(async () => {
			try {
				const response = await axiosConfig.post(
					`${import.meta.env.VITE_APP_API_URL}/chargebee/record-retention-progress`,
					{ ...payload, retentionId: session.retentionId }
				);
				if (response?.data?.retentionId) {
					session.retentionId = response.data.retentionId;
				}
			} catch {
				// Non-blocking background sync
			}
		});
		return session.pending;
	};

	const reset = () => {
		setStep(STEPS.CONFIRM);
		setReason('');
		setOtherText('');
		setLikelihood(null);
		setFeedback('');
		setAcknowledged(false);
		setCouponApplied(false);
		setCouponOffered(false);
		setOfferOffered(false);
		setOfferAccepted(false);
		setOfferType('');
		setSelectedPauseMonths(1);
		setSubmitting(false);
		offerAcceptedRef.current = false;
		offerTypeRef.current = '';
	};

	const recordLeftFlow = () => {
		if (trackingSessionRef.current.completed) return;
		saveRetentionProgress(step, {
			statusAfter: 'retained_modal_closed',
			outcome: 'left the flow',
			offerAccepted: false,
			pauseMonths: null,
			clientCompletedAt: new Date().toISOString()
		});
		markTrackingCompleted();
	};

	const handleNevermind = () => {
		recordLeftFlow();
		reset();
		onClose();
	};

	// X button / backdrop click: same as Nevermind unless the attempt already ended in an offer or cancel.
	const handleClose = () => {
		if (!(offerAcceptedRef.current || offerAccepted)) {
			recordLeftFlow();
		}
		reset();
		onClose();
	};

	const track = (eventName, meta = {}) =>
		trackManageSubscriptionEvent({
			eventName,
			subscriptionId: subscriptionDetails?.subscriptionId,
			planId,
			source: 'cancellation_retention_modal',
			meta
		});

	const handleDirectPause = async (months = 1) => {
		setSubmitting(true);
		try {
			const retentionId = await getRetentionId();
			const response = await axiosConfig.post(`${import.meta.env.VITE_APP_API_URL}/chargebee/pause-subscription`, {
				pauseOption: 'end_of_term',
				pauseMonths: months,
				retentionId,
				retentionSurvey: {
					retentionId,
					planId: planId || subscriptionDetails?.planId || '',
					billingCadence: isAnnual ? 'annual' : 'monthly',
					platform: isApple ? 'apple' : 'web',
					reason,
					otherText,
					likelihood,
					feedback,
					offerType: `pause_${months}_months`,
					pauseMonths: months,
					offerOffered: true,
					offerAccepted: true,
					couponOffered: false,
					statusAfter: 'retained_offer_accepted',
					outcome: 'kept with an offer',
					clientStartedAt: startedAtRef.current?.toISOString?.(),
					clientCompletedAt: new Date().toISOString(),
					source: 'cancellation_retention_modal'
				}
			});
			offerAcceptedRef.current = true;
			offerTypeRef.current = `pause_${months}_months`;
			setPauseScheduledLocal(true);
			setOfferAccepted(true);
			setOfferType(`pause_${months}_months`);
			track('retention_pause_accepted', { pauseMonths: months });
			Toast.success(
				response?.data?.message ||
					`Subscription will pause at the end of your billing cycle for ${months} month(s). We look forward to seeing you back!`
			);
			if (typeof onSubscriptionUpdated === 'function') {
				onSubscriptionUpdated(response.data);
			}
			markTrackingCompleted();
			handleClose();
		} catch (err) {
			Toast.error(err?.response?.data?.error || 'Failed to pause subscription');
		} finally {
			setSubmitting(false);
		}
	};

	const handleExtendTrial = async () => {
		setSubmitting(true);
		try {
			const retentionId = await getRetentionId();
			const response = await axiosConfig.post(`${import.meta.env.VITE_APP_API_URL}/chargebee/extend-trial`, {
				retentionId,
				planId: planId || subscriptionDetails?.planId || '',
				billingCadence: isAnnual ? 'annual' : 'monthly',
				platform: isApple ? 'apple' : 'web',
				reason,
				otherText,
				offerType: 'trial_extension_7d',
				statusAfter: 'retained_offer_accepted',
				outcome: 'kept with an offer',
				offerAccepted: true,
				couponOffered: false,
				pauseMonths: null,
				clientStartedAt: startedAtRef.current?.toISOString?.(),
				source: 'cancellation_retention_modal'
			});
			offerAcceptedRef.current = true;
			offerTypeRef.current = 'trial_extension_7d';
			setOfferAccepted(true);
			setOfferType('trial_extension_7d');
			track('retention_trial_extension_accepted', { reason });
			Toast.success(response?.data?.message || 'Your trial has been extended by 7 days!');
			if (typeof onSubscriptionUpdated === 'function') {
				onSubscriptionUpdated(response.data);
			}
			markTrackingCompleted();
			handleClose();
		} catch (err) {
			Toast.error(err?.response?.data?.error || 'Failed to extend trial');
		} finally {
			setSubmitting(false);
		}
	};

	const handleAddBonusMonth = async () => {
		setSubmitting(true);
		try {
			const retentionId = await getRetentionId();
			const response = await axiosConfig.post(`${import.meta.env.VITE_APP_API_URL}/chargebee/add-bonus-month`, {
				retentionId,
				planId: planId || subscriptionDetails?.planId || '',
				billingCadence: isAnnual ? 'annual' : 'monthly',
				platform: isApple ? 'apple' : 'web',
				reason,
				otherText,
				offerType: 'bonus_month_annual',
				statusAfter: 'retained_offer_accepted',
				outcome: 'kept with an offer',
				offerAccepted: true,
				couponOffered: true,
				pauseMonths: null,
				clientStartedAt: startedAtRef.current?.toISOString?.(),
				source: 'cancellation_retention_modal'
			});
			offerAcceptedRef.current = true;
			offerTypeRef.current = 'bonus_month_annual';
			setBonusMonthAddedLocal(true);
			setOfferAccepted(true);
			setOfferType('bonus_month_annual');
			track('retention_bonus_month_accepted', { reason });
			Toast.success(response?.data?.message || '1 bonus month added to your annual term!');
			if (typeof onSubscriptionUpdated === 'function') {
				onSubscriptionUpdated(response.data);
			}
			markTrackingCompleted();
			handleClose();
		} catch (err) {
			Toast.error(err?.response?.data?.error || 'Failed to add bonus month');
		} finally {
			setSubmitting(false);
		}
	};

	const handleClaimFreeMonth = async () => {
		setSubmitting(true);
		try {
			const retentionId = await getRetentionId();
			const response = await axiosConfig.post(`${import.meta.env.VITE_APP_API_URL}/chargebee/apply-retention-coupon`, {
				retentionId,
				couponCode: 'FREEMONTH',
				planId: planId || subscriptionDetails?.planId || '',
				billingCadence: isAnnual ? 'annual' : 'monthly',
				platform: isApple ? 'apple' : 'web',
				reason,
				otherText,
				offerType: 'free_month_monthly',
				statusAfter: 'retained_offer_accepted',
				outcome: 'kept with an offer',
				offerAccepted: true,
				couponOffered: true,
				couponApplied: true,
				pauseMonths: null,
				clientStartedAt: startedAtRef.current?.toISOString?.(),
				source: 'cancellation_retention_modal'
			});
			offerAcceptedRef.current = true;
			offerTypeRef.current = 'free_month_monthly';
			setRetentionCouponAppliedLocal(true);
			setCouponApplied(true);
			setOfferAccepted(true);
			setOfferType('free_month_monthly');
			track('retention_free_month_accepted', { reason });
			Toast.success(response?.data?.message || '100% discount applied to your next invoice!');
			if (typeof onSubscriptionUpdated === 'function') {
				onSubscriptionUpdated(response.data);
			}
			markTrackingCompleted();
			handleClose();
		} catch (err) {
			Toast.error(err?.response?.data?.error || 'Failed to apply free month discount');
		} finally {
			setSubmitting(false);
		}
	};

	const handleApplyDiscount = async code => {
		setSubmitting(true);
		try {
			const retentionId = await getRetentionId();
			const resolvedDiscountType = code === 'REJOIN-50' ? 'discount_50_monthly' : 'discount_25_annual';
			const response = await axiosConfig.post(`${import.meta.env.VITE_APP_API_URL}/chargebee/apply-retention-coupon`, {
				retentionId,
				couponCode: code,
				planId: planId || subscriptionDetails?.planId || '',
				billingCadence: isAnnual ? 'annual' : 'monthly',
				platform: isApple ? 'apple' : 'web',
				reason,
				otherText,
				offerType: resolvedDiscountType,
				statusAfter: 'retained_offer_accepted',
				outcome: 'kept with an offer',
				offerAccepted: true,
				couponOffered: true,
				couponApplied: true,
				pauseMonths: null,
				clientStartedAt: startedAtRef.current?.toISOString?.(),
				source: 'cancellation_retention_modal'
			});
			offerAcceptedRef.current = true;
			offerTypeRef.current = resolvedDiscountType;
			setRetentionCouponAppliedLocal(true);
			setCouponApplied(true);
			setOfferAccepted(true);
			setOfferType(resolvedDiscountType);
			track('retention_coupon_applied', { coupon: code, reason });
			Toast.success(response?.data?.message || 'Discount applied! It will reflect on your next invoice/renewal.');
			if (typeof onSubscriptionUpdated === 'function') {
				onSubscriptionUpdated(response.data);
			}
			markTrackingCompleted();
			handleClose();
		} catch (err) {
			Toast.error(err?.response?.data?.error || 'Failed to apply discount');
		} finally {
			setSubmitting(false);
		}
	};

	const handleCopyWebLink = () => {
		const couponCode = isAnnual ? 'REJOIN-25' : 'REJOIN-50';
		navigator.clipboard.writeText(`https://app.thegodfreymethod.com/plans?coupon=${couponCode}`);
		Toast.success('Web checkout link copied to clipboard!');
	};

	const handleConfirmCancel = async () => {
		if (!acknowledged) return;
		setSubmitting(true);
		try {
			const retentionId = await getRetentionId();
			const { offerType: finalOfferType, isEligible, noOfferReason } = resolveRetentionOffer(reason, getOfferOptions());
			const isCouponType = [
				'free_month_monthly',
				'bonus_month_annual',
				'discount_50_monthly',
				'discount_25_annual'
			].includes(finalOfferType);

			await onConfirmCancel({
				retentionId,
				reason,
				otherText,
				likelihood,
				feedback,
				acknowledged,
				couponApplied,
				couponOffered: isEligible && isCouponType && !noOfferReason,
				offerOffered: isEligible && !noOfferReason,
				offerAccepted: false,
				offerType: finalOfferType,
				noOfferReason: isApple ? 'apple' : noOfferReason || '',
				pauseMonths: null,
				isTrialUser: isTrial,
				isAppleUser: isApple,
				// Chargebee cancels a paused subscription immediately; record it that way.
				cancelOption: isPaused && !isApple ? 'immediately' : cancelOption,
				visitedSteps: Array.from(visitedStepsRef.current),
				clientStartedAt: startedAtRef.current?.toISOString?.() ?? null,
				clientCompletedAt: new Date().toISOString()
			});
			track('retention_cancel_confirmed', { reason, likelihood, cancelOption, offerType: finalOfferType });
			markTrackingCompleted();
			reset();
		} catch {
			// Handled by parent
		} finally {
			setSubmitting(false);
		}
	};

	const goNext = () => setStep(s => s + 1);

	const renderReasonOffer = () => {
		if (offerAccepted || couponApplied) {
			return (
				<div className="crm__offer-box crm__offer-box--success">
					<p>
						<CheckCircleOutlineIcon style={{ color: '#10b981', verticalAlign: 'middle', marginRight: '0.5rem' }} />
						Your offer has been applied successfully. We are excited to keep practicing with you!
					</p>
					<button className="crm__btn crm__btn--secondary" onClick={handleClose}>
						Return to My Account
					</button>
				</div>
			);
		}

		if (isApple) {
			const appleOffer = getAppleOfferDetails(reason, { isAnnual, isTrial });

			return (
				<div className="crm__apple-notice">
					<h4>
						<HelpOutlineIcon fontSize="small" />
						{appleOffer.title}
					</h4>
					<p>
						Because your subscription was purchased through the Apple App Store, Apple manages your billing directly and
						in-app discounts cannot be applied.
					</p>
					<p>
						However, you can claim <strong>{appleOffer.deal}</strong> when subscribing through our website. To take
						advantage of this deal, cancel your renewal in your Apple settings (
						<em>Settings &gt; Apple ID &gt; Subscriptions</em>) and re-subscribe directly on the web.
					</p>
					<div className="crm__actions">
						<button type="button" className="crm__btn crm__btn--primary" onClick={handleCopyWebLink}>
							<ContentCopyIcon fontSize="small" style={{ marginRight: '0.5rem' }} />
							Copy Web Checkout Link
						</button>
					</div>
				</div>
			);
		}

		const { offerType: currentOfferType, isEligible: currentIsEligible } = resolveRetentionOffer(
			reason,
			getOfferOptions()
		);

		if (!currentIsEligible || currentOfferType === 'none') {
			return (
				<div className="crm__offer-box">
					<p className="crm__body-text">
						{isPauseScheduled
							? 'A membership pause is already scheduled for your subscription.'
							: isBonusMonthAdded
								? 'A bonus month has already been added to your subscription.'
								: isRetentionCouponApplied
									? 'A retention discount has already been applied to your subscription.'
									: 'Thank you for sharing your feedback. You can continue with your cancellation below.'}
					</p>
				</div>
			);
		}

		if (reason === 'technical') {
			return (
				<div className="crm__offer-box">
					<p>
						We apologize for the technical difficulties you encountered. Our engineering and support team is ready to
						help resolve it.
					</p>
					<div className="crm__help-links">
						<a
							href={SocialMediaLinks.helpAndSupport}
							target="_blank"
							rel="noreferrer"
							className="crm__btn crm__btn--outline"
						>
							Visit Help Center
						</a>
						<a
							href="https://thegodfreymethod.zendesk.com/hc/en-us/requests/new"
							target="_blank"
							rel="noreferrer"
							className="crm__btn crm__btn--outline"
						>
							Open a Support Ticket
						</a>
					</div>
					{isTrial ? (
						!isTrialExtended ? (
							<button className="crm__btn crm__btn--primary" onClick={handleExtendTrial} disabled={submitting}>
								{submitting ? 'Applying…' : 'Claim 7-Day Trial Extension'}
							</button>
						) : (
							<p className="crm__body-text" style={{ marginTop: '0.75rem' }}>
								You have already received a 7-day trial extension. You can cancel your trial below anytime before your
								renewal to avoid being charged.
							</p>
						)
					) : !isPauseScheduled ? (
						<button className="crm__btn crm__btn--primary" onClick={() => handleDirectPause(1)} disabled={submitting}>
							{submitting ? 'Pausing…' : 'Pause for 1 Month While We Assist'}
						</button>
					) : (
						<p className="crm__body-text" style={{ marginTop: '0.75rem' }}>
							A membership pause is already scheduled for your subscription.
						</p>
					)}
				</div>
			);
		}

		if (isTrial) {
			if (!isTrialExtended && (reason === 'not_using' || reason === 'too_complicated')) {
				return (
					<div className="crm__offer-box">
						<p>
							<strong>Need more time to explore?</strong> We know life gets busy. We would love to offer you an extra 7
							days of full access—at no charge—so you can experience The Godfrey Method on your own timeline.
						</p>
						{reason === 'too_complicated' && (
							<a href={SocialMediaLinks.helpAndSupport} target="_blank" rel="noreferrer" className="crm__guide-box">
								<span>Quick-Start Guide &amp; Level 1 Classes</span>
								<OpenInNewIcon fontSize="small" />
							</a>
						)}
						<button className="crm__btn crm__btn--primary" onClick={handleExtendTrial} disabled={submitting}>
							{submitting ? 'Applying…' : 'Claim 7-Day Trial Extension'}
						</button>
					</div>
				);
			}

			return (
				<div className="crm__offer-box">
					<p>
						{isTrialExtended
							? 'You have already received a 7-day trial extension. You can cancel your trial below anytime before your renewal to avoid being charged.'
							: 'Thank you for trying The Godfrey Method. You can cancel your trial below anytime before your renewal to avoid being charged.'}
					</p>
				</div>
			);
		}

		if (reason === 'taking_break' || reason === 'other') {
			return (
				<div className="crm__offer-box">
					<p>
						<strong>Need a break?</strong> Keep your saved videos, custom playlists, and account history completely
						intact. Choose your pause duration—billing will pause immediately and resume automatically.
					</p>
					<div className="crm__pause-duration-container">
						<span className="crm__pause-label">Choose pause duration:</span>
						<div className="crm__pause-duration-group">
							{[1, 2, 3].map(months => (
								<button
									key={months}
									type="button"
									className={`crm__pause-pill${selectedPauseMonths === months ? ' crm__pause-pill--active' : ''}`}
									onClick={() => setSelectedPauseMonths(months)}
								>
									{months} {months === 1 ? 'Month' : 'Months'}
								</button>
							))}
						</div>
					</div>
					<button
						className="crm__btn crm__btn--primary"
						onClick={() => handleDirectPause(selectedPauseMonths)}
						disabled={submitting}
					>
						{submitting
							? 'Pausing…'
							: `Pause for ${selectedPauseMonths} ${selectedPauseMonths === 1 ? 'Month' : 'Months'}`}
					</button>
				</div>
			);
		}

		if (reason === 'not_using') {
			return (
				<div className="crm__offer-box">
					<p>
						<strong>Enjoy 1 Free Month on Us!</strong> We would love for you to give it another shot without any
						financial commitment.
						{isAnnual
							? ' We will add 1 full bonus month to your annual membership at zero additional charge.'
							: ' Your next monthly invoice will be 100% free of charge.'}
					</p>
					<button
						className="crm__btn crm__btn--primary"
						onClick={isAnnual ? handleAddBonusMonth : handleClaimFreeMonth}
						disabled={submitting}
					>
						{submitting ? 'Applying…' : isAnnual ? 'Claim 1 Bonus Month' : 'Claim My Free Month'}
					</button>
				</div>
			);
		}

		if (reason === 'too_expensive' || reason === 'low_value') {
			return (
				<div className="crm__offer-box">
					<p>
						<strong>We want The Godfrey Method to fit your budget.</strong>
						{isAnnual
							? ' Enjoy 25% off your upcoming annual renewal invoice.'
							: ' Enjoy 50% off your next 3 monthly invoices.'}
					</p>
					<button
						className="crm__btn crm__btn--primary"
						onClick={() => handleApplyDiscount(isAnnual ? 'REJOIN-25' : 'REJOIN-50')}
						disabled={submitting}
					>
						{submitting ? 'Applying…' : isAnnual ? 'Apply 25% Off Renewal' : 'Apply 50% Off (3 Months)'}
					</button>
				</div>
			);
		}

		if (reason === 'too_complicated') {
			return (
				<div className="crm__offer-box">
					<p>
						<strong>Let&apos;s simplify your journey!</strong> Explore our step-by-step Quick-Start guide to find the
						perfect starting point, plus enjoy 1 full month free on us.
					</p>
					<a href={SocialMediaLinks.helpAndSupport} target="_blank" rel="noreferrer" className="crm__guide-box">
						<span>Quick-Start Guide &amp; Level 1 Tutorials</span>
						<OpenInNewIcon fontSize="small" />
					</a>
					<button
						className="crm__btn crm__btn--primary"
						onClick={isAnnual ? handleAddBonusMonth : handleClaimFreeMonth}
						disabled={submitting}
					>
						{submitting ? 'Applying…' : isAnnual ? 'Claim 1 Bonus Month' : 'Claim My Free Month'}
					</button>
				</div>
			);
		}

		return (
			<p className="crm__body-text">
				Thank you for sharing your feedback. You can continue with your cancellation below.
			</p>
		);
	};

	if (!open) return null;

	return (
		<div className="crm__overlay" onClick={e => e.target === e.currentTarget && handleClose()}>
			<div className="crm__panel">
				<button type="button" className="crm__close" onClick={handleClose}>
					×
				</button>

				{/* Step 0 — Are you sure? */}
				{step === STEPS.CONFIRM && (
					<div className="crm__step">
						<h3>Are you sure you want to cancel?</h3>
						<p className="crm__subtitle">Here&apos;s what you&apos;ll lose:</p>
						<ul className="crm__loss-list">
							<li>Access to our exclusive On Demand library of 250+ videos</li>
							<li>Videos featuring guest instructors</li>
							<li>Featured and Personal playlists</li>
							<li>Saved Videos and Community Chat</li>
							{isPlus && <li>Access to our full-length Classes</li>}
							{isStudio && <li>Teacher Resource Center</li>}
						</ul>
						<div className="crm__actions">
							<button className="crm__btn crm__btn--primary" onClick={handleNevermind}>
								Nevermind, Keep Subscription
							</button>
							<button
								className="crm__btn crm__btn--ghost"
								onClick={() => {
									track('retention_confirm_continue');
									goNext();
								}}
							>
								Continue Cancellation
							</button>
						</div>
					</div>
				)}

				{/* Step 1 — Reason */}
				{step === STEPS.REASON && (
					<div className="crm__step">
						<h3>What is the primary reason for leaving?</h3>
						<div className="crm__reason-list">
							{REASONS.map(r => (
								<button
									key={r.value}
									type="button"
									className={`crm__reason-btn${reason === r.value ? ' crm__reason-btn--active' : ''}`}
									onClick={() => {
										setReason(r.value);
										setCouponApplied(false);
										setOfferAccepted(false);
									}}
								>
									{r.label}
								</button>
							))}
						</div>
						{reason === 'other' && (
							<textarea
								className="crm__textarea"
								placeholder="Please tell us more…"
								value={otherText}
								onChange={e => setOtherText(e.target.value)}
								rows={3}
							/>
						)}
						<div className="crm__actions crm__actions--top-gap">
							<button
								className="crm__btn crm__btn--ghost"
								onClick={() => {
									const { offerType: nextOfferType, isEligible: nextIsEligible } = resolveRetentionOffer(
										reason,
										getOfferOptions()
									);

									track('retention_reason_continue', { reason });
									saveRetentionProgress(STEPS.REASON, { reason, otherText });
									if (isStudio || !nextIsEligible || nextOfferType === 'none') {
										setStep(STEPS.LIKELIHOOD);
									} else {
										goNext();
									}
								}}
								disabled={!reason || (reason === 'other' && !otherText.trim())}
							>
								Continue Cancellation
							</button>
						</div>
					</div>
				)}

				{/* Step 2 — Reason Offer */}
				{step === STEPS.REASON_OFFER && (
					<div className="crm__step">
						<h3>Before you go...</h3>
						{renderReasonOffer()}
						<div className="crm__actions crm__actions--top-gap">
							<button
								className="crm__btn crm__btn--ghost"
								onClick={() => {
									track('retention_offer_continue', { reason });
									saveRetentionProgress(STEPS.REASON_OFFER, {
										offerOffered: true,
										offerAccepted: false
									});
									goNext();
								}}
							>
								Continue Cancellation
							</button>
						</div>
					</div>
				)}

				{/* Step 3 — Likelihood */}
				{step === STEPS.LIKELIHOOD && (
					<div className="crm__step">
						<h3>How likely are you to return?</h3>
						<p className="crm__subtitle">1 = Very Unlikely &nbsp;·&nbsp; 10 = Very Likely</p>
						<div className="crm__scale">
							{Array.from({ length: 10 }, (_, i) => i + 1).map(n => (
								<button
									key={n}
									type="button"
									className={`crm__scale-btn${likelihood === n ? ' crm__scale-btn--active' : ''}`}
									onClick={() => {
										setLikelihood(n);
									}}
								>
									{n}
								</button>
							))}
						</div>
						<div className="crm__actions crm__actions--top-gap">
							<button
								className="crm__btn crm__btn--ghost"
								onClick={() => {
									track('retention_likelihood_continue', { likelihood });
									saveRetentionProgress(STEPS.LIKELIHOOD, { likelihood });
									goNext();
								}}
								disabled={likelihood === null}
							>
								Continue Cancellation
							</button>
						</div>
					</div>
				)}

				{/* Step 4 — Feedback */}
				{step === STEPS.FEEDBACK && (
					<div className="crm__step">
						<h3>Would you like to share more about your experience?</h3>
						<p className="crm__subtitle">Optional</p>
						<textarea
							className="crm__textarea"
							placeholder="Your feedback helps us improve…"
							value={feedback}
							onChange={e => setFeedback(e.target.value)}
							rows={4}
						/>
						<div className="crm__actions crm__actions--top-gap">
							<button
								className="crm__btn crm__btn--ghost"
								onClick={() => {
									track('retention_feedback_continue');
									saveRetentionProgress(STEPS.FEEDBACK, { feedback });
									goNext();
								}}
							>
								Continue Cancellation
							</button>
						</div>
					</div>
				)}

				{/* Step 5 — Last Chance */}
				{step === STEPS.LAST_CHANCE && (
					<div className="crm__step">
						{isApple ? (
							<>
								<h3>Final Step: Apple Subscription</h3>
								<div className="crm__apple-notice" style={{ marginBottom: '1.2rem' }}>
									<h4>
										<HelpOutlineIcon fontSize="small" />
										Action Required on Your Apple Device
									</h4>
									<p>
										Because your subscription is billed through Apple, we cannot cancel or modify it directly. To
										prevent any future charges, you must turn off auto-renewal in your Apple ID settings:
									</p>
									<ol
										style={{
											margin: '0.2rem 0 0 1.2rem',
											padding: 0,
											fontSize: '0.88rem',
											color: 'rgba(226, 236, 245, 0.9)',
											lineHeight: '1.6'
										}}
									>
										<li>
											Open the <strong>Settings</strong> app on your iPhone or iPad.
										</li>
										<li>
											Tap your <strong>Name / Apple ID</strong> at the top, then tap <strong>Subscriptions</strong>.
										</li>
										<li>
											Select <strong>The Godfrey Method</strong> and tap <strong>Cancel Subscription</strong>.
										</li>
									</ol>
								</div>
								<label className="crm__checkbox-label">
									<input type="checkbox" checked={acknowledged} onChange={e => setAcknowledged(e.target.checked)} />
									<span>
										I understand that I must complete my cancellation directly in my Apple ID settings, and that
										submitting this form records my feedback but does not cancel Apple billing.
									</span>
								</label>
								<div className="crm__actions crm__actions--top-gap">
									<button
										className="crm__btn crm__btn--danger"
										onClick={handleConfirmCancel}
										disabled={!acknowledged || submitting}
									>
										{submitting ? 'Submitting…' : 'I Understand, Record My Feedback'}
									</button>
									<button className="crm__btn crm__btn--primary" onClick={handleNevermind}>
										Nevermind, Keep My Subscription
									</button>
								</div>
							</>
						) : (
							<>
								<h3>Last Chance!</h3>
								{isPaused ? (
									<p className="crm__body-text">
										Your subscription is currently paused, so cancelling will end it immediately. Your access will end
										now and your subscription will not resume.
									</p>
								) : (
									<p className="crm__body-text">
										Your subscription will be cancelled at the end of your current billing term. You&apos;ll retain full
										access until {cancellationDateLabel || 'the end of your current billing term'}.
									</p>
								)}
								<label className="crm__checkbox-label">
									<input type="checkbox" checked={acknowledged} onChange={e => setAcknowledged(e.target.checked)} />
									<span>
										{isPaused
											? 'I understand that by proceeding with the cancellation, I will lose access to my account immediately, including all saved videos, personal playlists, and account information since joining.'
											: 'I understand that by proceeding with the cancellation, I will lose access to my account at the end of my current billing term, including all saved videos, personal playlists, and account information since joining.'}
									</span>
								</label>
								<div className="crm__actions crm__actions--top-gap">
									<button
										className="crm__btn crm__btn--danger"
										onClick={handleConfirmCancel}
										disabled={!acknowledged || submitting}
									>
										{submitting ? 'Cancelling…' : 'Cancel my Subscription'}
									</button>
									<button className="crm__btn crm__btn--primary" onClick={handleNevermind}>
										Nevermind, Keep My Subscription
									</button>
								</div>
							</>
						)}
					</div>
				)}

				<div className="crm__progress">
					{Object.values(STEPS)
						.filter(s => {
							if (isStudio) return s !== STEPS.REASON_OFFER;
							const { isEligible } = resolveRetentionOffer(reason, getOfferOptions());
							if (!isEligible && reason && s === STEPS.REASON_OFFER) return false;
							return true;
						})
						.map(s => (
							<span
								key={s}
								className={`crm__progress-dot${step === s ? ' crm__progress-dot--active' : step > s ? ' crm__progress-dot--done' : ''}`}
							/>
						))}
				</div>
			</div>
		</div>
	);
}

export default CancellationRetentionModal;
